'use client';
import React, { createContext, useContext, useMemo, useRef, useState } from 'react';
import { useStore } from 'zustand';
import { EyeInvisibleOutlined, EyeOutlined, LockOutlined, SettingOutlined, UnlockOutlined } from '@ant-design/icons';
import type {
  DragEndEvent,
  DragStartEvent,
  CollisionDetection,
  Collision,
  Modifier,
  UniqueIdentifier,
} from '@dnd-kit/core';
import { DndContext, DragOverlay, useSensor, useSensors, MouseSensor, TouchSensor } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { Table, Button, Tooltip } from 'antd';
import type { ChartId, ChartInfo, ShapeSelection } from '..';
import { entries, ShapeType } from '..';
import styles, { DeleteShapeButton, DynamicSvg, useChartShapes, getShortSymbolName, ucFirst } from '..';
import { useChartUIContext } from '../contexts';
import { useGlobalShapeSelector } from '../contexts/GlobalShapesContext';
import { useChartUIStoreSelector } from '../stores/chartUIStore';
import type { ColumnsType } from 'antd/lib/table/interface';
import type { OrderLineProps, RenderNodesMap } from '../types/commontypes';
import { moveItem, updateShapesOrderOptimized } from '@/utils/getShapeById';
import { useDeleteShape } from '@/utils/deleteShape';
import { useHideShape } from '@/utils/hideShape';
import { useLockShape } from '@/utils/lockShape';

type RecordType = {
  key: string;
  label: string;
  chartId: ChartId;
  isHeader: boolean;
  shape?: ShapeType;
  order?: number;
  isCommon?: boolean;
  positionType?: OrderLineProps['positionType'];
  isHidden?: boolean;
  isLocked?: boolean;
};

// ─── Row context & stable component ────────────────────────────────
// Row를 ShapeListView 밖에 정의해야 매 렌더마다 새 함수가 생성되지 않는다.
// 새 함수가 생성되면 Ant Design Table이 모든 row를 unmount/remount하며,
// 이때 ref callback → setState → 리렌더 → 새 Row → unmount/remount 무한 루프가 발생한다.

interface SortableRowData {
  dataSource: RecordType[];
  selectedShapeUuid: string | null;
  activeId: string | null;
  overId: string | null;
  setSelectedShape: React.Dispatch<React.SetStateAction<ShapeSelection | null>>;
  timeframe: string;
}

const SortableRowContext = createContext<React.RefObject<SortableRowData | null> | null>(null);

const SortableRow = ({
  children,
  ...props
}: React.HTMLAttributes<HTMLTableRowElement> & {
  'data-row-key'?: string;
  style?: React.CSSProperties;
  className?: string;
}) => {
  const ctxRef = useContext(SortableRowContext)!;
  const { attributes, listeners, setNodeRef } = useSortable({ id: props['data-row-key'] ?? '' });

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const rowKey = props['data-row-key'] as string;
  const isHeader = ('' + rowKey)?.startsWith('header-');

  // Read latest values from ref (avoids stale closure)
  const { dataSource, selectedShapeUuid, activeId, overId, setSelectedShape, timeframe } = ctxRef.current!;

  // Inline getDropPosition logic
  let dropPosition: 'before' | 'after' | null = null;
  if (activeId && overId && overId === rowKey && activeId !== rowKey) {
    const activeIndex = dataSource.findIndex(i => i.key === activeId);
    const overIndex = dataSource.findIndex(i => i.key === overId);
    if (activeIndex !== -1 && overIndex !== -1) {
      dropPosition = activeIndex < overIndex ? 'after' : 'before';
    }
  }

  const style: React.CSSProperties = {
    ...props.style,
    ...(!isHeader ? { cursor: 'grab', touchAction: 'none' } : {}),
  };

  const handleRowMouseDown = (e: React.MouseEvent) => {
    const key = props['data-row-key'] ?? '';
    if (!isHeader) {
      const record = dataSource.find(i => i.key === key);
      if (record) {
        setSelectedShape({
          // common shape(VertLine 등)는 chartId_common에 저장되므로 injectSelected 매칭을 위해 'chartId_common' 사용

          chartId: record.isCommon ? 'chartId_common' : record.chartId,
          uuid: key,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          type: record.shape as ShapeType,
          // common shape도 실제 timeframe을 전달해야 ContentLayout의 hasShapeInThisChart 조건 통과
          timeframe: timeframe || '',
        });
      }
    }
    if (!isHeader && listeners?.onMouseDown) {
      listeners.onMouseDown(e);
    }
  };

  const dropIndicatorBoxShadow =
    dropPosition === 'before'
      ? 'inset 0 2px 0 0 var(--accentColor, #1890ff)'
      : dropPosition === 'after'
        ? 'inset 0 -2px 0 0 var(--accentColor, #1890ff)'
        : undefined;

  return (
    <tr
      {...props}
      ref={setNodeRef}
      style={{
        ...style,
        boxShadow: dropIndicatorBoxShadow,
      }}
      {...attributes}
      {...(!isHeader ? { ...listeners, onMouseDown: undefined } : {})}
      className={`
          ${props.className || ''}
          ${props['data-row-key'] === selectedShapeUuid ? styles.selectedRow : ''}
          ${isHeader ? styles.indicatorHeaderRow : 'ml-2'}`}
      onMouseDown={handleRowMouseDown}
    >
      {children}
    </tr>
  );
};

/** Ant Design Table components 설정 — 모듈 레벨 상수로 참조 안정성 보장 */
const tableComponents = { body: { row: SortableRow } };

export const ShapeListView = React.memo(() => {
  // Get from contexts
  const { symbol, timeframe, shapeStore } = useChartShapes();
  const { chartUIStore } = useChartUIContext();
  const chartInfos = useChartUIStoreSelector(chartUIStore, s => s.chartInfos);
  const renderNodes = useStore(shapeStore, s => s.renderNodes);
  const setRenderNodes = useStore(shapeStore, s => s.setRenderNodes);
  const selectedShape = useGlobalShapeSelector(s => s.selectedShape);
  const setSelectedShape = useGlobalShapeSelector(s => s.setSelectedShape);
  const { handleDelete } = useDeleteShape();
  const { handleHide } = useHideShape();
  const { toggleLock } = useLockShape();
  const setIsPopupVisible = useChartUIStoreSelector(chartUIStore, s => s.setIsPopupVisible);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  // Configure sensors with activation constraint
  // Requires 5px movement before drag starts, allowing clicks to work normally
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
  );

  // Memoized + Ordered flat shape list

  // Flattened dataSource for always-expanded table
  // Group by subChart (chartInfos order), use chartInfos.label for header
  const dataSource = useMemo<RecordType[]>(() => {
    if (!chartInfos || !renderNodes) return [];
    const result: RecordType[] = [];
    /** Pre-group common shapes by originChartId */
    const commonByChartId: Record<ChartId, RecordType[]> = {};
    const commonShapes = renderNodes.chartId_common || {};

    entries(commonShapes).forEach(([type, shapes]) => {
      shapes.forEach(shape => {
        const chartId = shape.type === ShapeType.VERT_LINE ? shape.originChartId : undefined;
        if (!chartId) return;

        (commonByChartId[chartId] ??= []).push({
          key: shape.uuid,
          label: type,
          chartId,
          shape: type,
          order: shape.order,
          isLocked: shape.locked,
          isHeader: false,
          isCommon: true,
        });
      });
    });

    // Iterate over chartInfos (ordered list of all sub-charts)
    chartInfos
      .filter((info: ChartInfo) => info.chartId)
      .forEach(({ chartId, label }: ChartInfo) => {
        // Add subChart header (always show all sub-charts)
        if (!chartId) return;
        result.push({
          key: `header-${chartId}`,
          label,
          chartId,
          isHeader: true,
        });

        /** Shapes directly under chart */
        const chartShapes = renderNodes[chartId] || {};
        const localShapes = entries(chartShapes).flatMap(([type, shapes]) =>
          shapes.map(shape => ({
            key: shape.uuid,
            label: type,
            chartId,
            shape: type,
            positionType: 'positionType' in shape ? shape.positionType : undefined,
            order: shape.order,
            isHeader: false,
            isHidden: shape.hidden,
            isLocked: shape.locked,
          })),
        );

        /** Merge local + common */
        [...localShapes, ...(commonByChartId[chartId] || [])]
          .sort((a, b) => (b.order ?? 0) - (a.order ?? 0))
          .forEach(shape => result.push(shape));
      });
    return result;
  }, [chartInfos, renderNodes]);

  // Custom modifier to restrict drag within the same chartId group
  const restrictToSameGroup: Modifier = ({ transform, draggingNodeRect }) => {
    if (!activeId || !draggingNodeRect) return transform;

    const activeItem = dataSource.find(i => i.key === activeId);
    if (!activeItem || activeItem.isHeader) return transform;

    // Find all shapes in the same chartId group
    const sameGroupItems = dataSource.filter(i => !i.isHeader && i.chartId === activeItem.chartId);

    // Even if there's only 1 item, we still need to restrict it to its group
    if (sameGroupItems.length === 0) return transform;

    // Get DOM elements for the first and last items in the group
    const firstItemKey = sameGroupItems[0].key;
    const lastItemKey = sameGroupItems[sameGroupItems.length - 1].key;

    // Find the table rows by data-row-key attribute
    const firstRow = document.querySelector(`tr[data-row-key="${firstItemKey}"]`);
    const lastRow = document.querySelector(`tr[data-row-key="${lastItemKey}"]`);

    if (!firstRow || !lastRow) return transform;

    const firstRect = firstRow.getBoundingClientRect();
    const lastRect = lastRow.getBoundingClientRect();

    // Get the height of the drag overlay for padding
    const overlayHeight = draggingNodeRect.height;

    // Calculate boundaries (relative to the dragging element's original position)
    // For single item groups, allow minimal movement within its own row area
    // For multi-item groups, extend boundaries by overlay height
    const minY = firstRect.top - draggingNodeRect.top - (sameGroupItems.length > 1 ? overlayHeight : 0);
    const maxY = lastRect.bottom - draggingNodeRect.bottom + (sameGroupItems.length > 1 ? overlayHeight : 0);

    // Clamp the transform.y to stay within group boundaries
    const clampedY = Math.max(minY, Math.min(maxY, transform.y));

    return {
      ...transform,
      y: clampedY,
    };
  };

  // Custom collision detection that only allows drops within the same group
  // Requires pointer to cross past 50% of the target row's height before collision is detected
  // Returns only the most appropriate collision (furthest in drag direction)
  const customCollisionDetection: CollisionDetection = args => {
    const { active, droppableRects, droppableContainers, pointerCoordinates } = args;
    const activeItem = dataSource.find(i => i.key === active.id);
    if (!activeItem || activeItem.isHeader || !pointerCoordinates) return [];

    const activeIndex = dataSource.findIndex(i => i.key === active.id);
    const collisions: { collision: Collision; overIndex: number }[] = [];

    for (const container of droppableContainers) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const id = container.id as string;
      const overItem = dataSource.find(i => i.key === id);

      // Skip headers and items from different chartIds
      if (!overItem || overItem.isHeader || overItem.chartId !== activeItem.chartId) {
        continue;
      }

      // Skip self
      if (id === active.id) continue;

      const rect = droppableRects.get(id);
      if (!rect) continue;

      const overIndex = dataSource.findIndex(i => i.key === id);
      const isDraggingDown = activeIndex < overIndex;

      // Calculate the threshold point (50% of row height)
      const threshold = rect.top + rect.height * 0.5;

      const isColliding = isDraggingDown
        ? pointerCoordinates.y > threshold // Dragging down: pointer below 50%
        : pointerCoordinates.y < threshold; // Dragging up: pointer above 50%

      if (isColliding) {
        collisions.push({
          collision: { id, data: { droppableContainer: container } },
          overIndex,
        });
      }
    }

    // If multiple collisions, pick the one furthest in the drag direction
    if (collisions.length === 0) return [];

    // Sort by index: ascending for drag down (pick largest), descending for drag up (pick smallest)
    collisions.sort((a, b) => a.overIndex - b.overIndex);

    // Dragging down: return the collision with highest index (furthest down)
    // Dragging up: return the collision with lowest index (furthest up)
    const isDraggingDown = collisions[collisions.length - 1].overIndex > activeIndex;
    const bestCollision = isDraggingDown
      ? collisions[collisions.length - 1] // Furthest down
      : collisions[0]; // Furthest up

    return [bestCollision.collision];
  };

  // Handle drag start
  const onDragStart = (event: DragStartEvent) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    setActiveId(event.active.id as string);
  };

  // Handle drag over for drop indicator
  const onDragOver = (event: { over: { id: UniqueIdentifier } | null }) => {
    const { over } = event;
    if (over) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      setOverId(over.id as string);
    } else {
      setOverId(null);
    }
  };

  // Handle the drag-and-drop operation for reordering shapes
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    setOverId(null);

    if (!over || active.id === over.id) return;

    // Build lookup map once
    const dataSourceMap = new Map<UniqueIdentifier, RecordType>(dataSource.map(item => [item.key, item]));

    const activeItem = dataSourceMap.get(active.id);
    const overItem = dataSourceMap.get(over.id);

    if (!activeItem || !overItem) return;

    const activeChartId = activeItem.isCommon ? overItem.chartId : activeItem.chartId;

    const notOnSameChart = activeItem.chartId !== overItem.chartId && (!overItem.isCommon || !activeItem.isCommon);

    if (notOnSameChart) return;

    // Filter only relevant items (single pass)
    const chartIdItems = dataSource.filter(item => !item.isHeader && item.chartId === activeItem.chartId);

    const activeIndex = chartIdItems.findIndex(i => i.key === active.id);
    const overIndex = chartIdItems.findIndex(i => i.key === over.id);

    if (activeIndex === -1 || overIndex === -1) return;

    const newArray = moveItem(chartIdItems, activeIndex, overIndex);

    const updatedShapes = updateShapesOrderOptimized(renderNodes, newArray, activeChartId);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    setRenderNodes(updatedShapes as RenderNodesMap);
  };

  // Get active item for DragOverlay
  const activeItem = useMemo(() => {
    if (!activeId) return null;
    return dataSource.find(i => i.key === activeId);
  }, [activeId, dataSource]);

  // Get the ID of the currently selected shape
  const selectedShapeUuid = selectedShape?.uuid ?? null;

  // Ref-based data bridge for SortableRow (avoids context re-render overhead)
  const sortableRowDataRef = useRef<SortableRowData>({
    dataSource,
    selectedShapeUuid,
    activeId,
    overId,
    setSelectedShape: setSelectedShape,
    timeframe,
  });
  sortableRowDataRef.current = {
    dataSource,
    selectedShapeUuid,
    activeId,
    overId,
    setSelectedShape: setSelectedShape,
    timeframe,
  };

  // Columns configuration for the Table component (single column with flex layout)
  const columns: ColumnsType<RecordType> = [
    {
      key: 'content',
      dataIndex: 'label',
      render: (text: string, record: RecordType) => {
        if (record.isHeader) {
          // show "short Symbol - timeframe" for Candle Chart
          const isCandleChart = chartInfos.find((info: ChartInfo) => info.chartId === record.chartId)?.isCandleChart;
          return (
            <div style={{ fontWeight: 600 }}>
              {isCandleChart ? `${getShortSymbolName(symbol)}, ${timeframe}` : record.label}
            </div>
          );
        }

        // Shape row: flex layout with icon + label + action buttons
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const type = record.shape as ShapeType;

        // Stop mouse events from bubbling to prevent drag activation
        const handleMouseDown = (e: React.MouseEvent) => {
          e.stopPropagation();
        };

        //Hide settings button if Order Line or Measure, as they have no editable properties for now
        const hideSettings = type === ShapeType.ORDER_LINE || type === ShapeType.MEASURE;

        const isHidden = record.isHidden;
        const isLocked = record.isLocked;

        return (
          <div className={styles.shapeRow}>
            {/* Icon + Label (takes remaining space, with ellipsis) */}
            <div className={styles.shapeLabel}>
              <DynamicSvg
                iconName={record.positionType ? `${type}${ucFirst(record.positionType)}` : type}
                width={24}
                height={24}
                style={{ flexShrink: 0 }}
              />
              <span className={styles.shapeLabelText}>{text}</span>
            </div>

            {/* Action buttons (hidden by default, shown on row hover via CSS) */}
            <div className={styles.shapeActions} onMouseDown={handleMouseDown}>
              {!hideSettings && (
                <>
                  <Tooltip title="Settings">
                    <Button
                      type="text"
                      icon={<SettingOutlined />}
                      onMouseDown={handleMouseDown}
                      onClick={() => {
                        // Select the shape first, then open property popup
                        setSelectedShape({
                          chartId: record.isCommon ? 'chartId_common' : record.chartId,
                          uuid: record.key,
                          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                          type: record.shape as ShapeType,
                          timeframe: record.isCommon ? '' : timeframe || '',
                        });
                        setIsPopupVisible(true);
                      }}
                      className={styles.iconBtn}
                    />
                  </Tooltip>
                  {/* Lock / Unlock */}
                  <Tooltip title={isLocked ? 'Unlock' : 'Lock'}>
                    <Button
                      type="text"
                      className={`${styles.iconBtn} ${isLocked ? styles.locked : ''}`}
                      icon={isLocked ? <LockOutlined /> : <UnlockOutlined />}
                      onClick={() => toggleLock(record.chartId, record.shape!, record.key)}
                    />
                  </Tooltip>

                  {/* Hide / Show */}
                  <Tooltip title={isHidden ? 'Show' : 'Hide'}>
                    <Button
                      type="text"
                      className={`${styles.iconBtn} ${isHidden ? styles.hidden : ''}`}
                      icon={isHidden ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                      onMouseDown={handleMouseDown}
                      onClick={() => handleHide(record.key)}
                    />
                  </Tooltip>
                </>
              )}
              <DeleteShapeButton
                onDelete={() => handleDelete(record.key)}
                className={styles.iconBtn}
                locked={!!isLocked}
              />
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className={`v-grid-hm`}>
      <div
        className="text-lg text-center py-3 font-bold border-b"
        style={{
          backgroundColor: 'var(--buttonColor)',
          borderColor: 'var(--borderColor)',
          fontSize: 'var(--fontSizeSmallTab)',
          color: 'var(--activeTabTextColor)',
          marginTop: '-1px',
        }}
      >
        Drawing List
      </div>
      <DndContext
        sensors={sensors}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
        collisionDetection={customCollisionDetection}
        modifiers={[restrictToVerticalAxis, restrictToSameGroup]}
      >
        <SortableContext items={dataSource.map(i => i.key)} strategy={verticalListSortingStrategy}>
          <div
            className={`${styles.table_list_wrapper}`}
            data-key={`${symbol}-${timeframe}`}
            style={{ height: '100%', overflowY: 'auto' }}
          >
            <SortableRowContext.Provider value={sortableRowDataRef}>
              <Table
                showHeader={false}
                components={tableComponents}
                rowKey="key"
                columns={columns}
                dataSource={dataSource}
                pagination={false}
              />
            </SortableRowContext.Provider>
          </div>
        </SortableContext>

        {/* DragOverlay - shows the dragged item (semi-transparent) */}
        <DragOverlay>
          {activeItem ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                backgroundColor: '#fff',
                border: '1px solid var(--accentColor, #1890ff)',
                borderRadius: 4,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                cursor: 'grabbing',
                opacity: 0.3,
              }}
            >
              {/* eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion */}
              <DynamicSvg iconName={activeItem.shape as string} width={24} height={24} />
              <span>{activeItem.label}</span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
});
